import { getPaidStatus } from '@trello/organizations';
import { getAccountType, getMaxPaidStatus, Login } from '@trello/members';
import { getElapsedDaysFromId } from '@trello/dates';

// Contextual data types
export interface Member {
  id: string;
  logins: Login[];
  organizations: MemberOrganization[];
  products: number[];
}

export interface Organization {
  billableMemberCount?: number | null;
  id: string;
  products: number[];
  teamType?: string | null;
}

export interface Workspace {
  billableMemberCount?: number | null;
  id: string;
  products: number[];
  teamType?: string | null;
}
interface MemberOrganization {
  products: number[];
}

// Contextual default attribute types
interface DefaultMemberAttributes {
  accountType: 'business' | 'personal';
  maxPaidStatus: 'bc' | 'enterprise' | 'free' | 'gold' | 'standard';
}

interface DefaultOrgAttributes {
  billableMemberCount: number | null | undefined;
  paidStatus: 'bc' | 'enterprise' | 'free' | 'standard';
  teamAgeInDays: number;
  teamType: string | null | undefined;
}
interface DefaultWorkspaceAttributes {
  billableMemberCount: number | null | undefined;
  paidStatus: 'bc' | 'enterprise' | 'free' | 'standard';
  teamAgeInDays: number;
  teamType: string | null | undefined;
}
export interface DefaultAnalyticsContext {
  member?: DefaultMemberAttributes;
  organization?: DefaultOrgAttributes;
  workspace?: DefaultWorkspaceAttributes;
}

const getDefaultMemberAttributes = ({
  logins,
  organizations,
  products,
}: Member): DefaultMemberAttributes => {
  const accountType = getAccountType(logins);
  const maxPaidStatus = getMaxPaidStatus(organizations, products);

  return {
    accountType,
    maxPaidStatus,
  };
};

const getDefaultOrgAttributes = ({
  billableMemberCount,
  id,
  products,
  teamType,
}: Organization): DefaultOrgAttributes => {
  const paidStatus = getPaidStatus(products);
  const teamAgeInDays = getElapsedDaysFromId(id);

  return {
    billableMemberCount,
    paidStatus,
    teamAgeInDays,
    teamType,
  };
};

const getDefaultWorkspaceAttributes = ({
  billableMemberCount,
  id,
  products,
  teamType,
}: Workspace): DefaultWorkspaceAttributes => {
  const paidStatus = getPaidStatus(products);
  const teamAgeInDays = getElapsedDaysFromId(id);

  return {
    billableMemberCount,
    paidStatus,
    teamAgeInDays,
    teamType,
  };
};

export const getDefaultAnalyticsContext = ({
  member,
  organization,
  workspace,
}: {
  member?: Member | null;
  organization?: Organization | null;
  workspace?: Workspace | null;
}): DefaultAnalyticsContext => {
  const defaultAnalyticsContext: DefaultAnalyticsContext = {};

  if (member)
    defaultAnalyticsContext.member = getDefaultMemberAttributes(member);
  if (organization)
    defaultAnalyticsContext.organization = getDefaultOrgAttributes(
      organization,
    );
  if (workspace)
    defaultAnalyticsContext.workspace = getDefaultWorkspaceAttributes(
      workspace,
    );
  return defaultAnalyticsContext;
};
